import { TicketBasket } from "@shared/modules/basket/types";
import Repository from "@shared/services/api/abstracts/Repository";
import { UrlRouteMap } from "@shared/services/api/types";
import {
  GetTaskFromJobIdReturnType,
  TaskedEndpointResponse,
} from "@shared/services/loaders/api";
import { taskLoader } from "@shared/services/loaders/task-loader";
import { AxiosRequestConfig } from "axios";
import { venueBasedUrl } from "@shared/services/api/utils/RepositoryUrlUtils";
import {
  BulkSendHoldLinkEmailPayload,
  BulkSendHoldLinkEmailResponse,
  VenueBasedTicketBasketHoldsSplitSerializer,
} from "../types";

export default class VenueBasedTicketBasketHoldsRepository extends Repository<TicketBasket> {
  public readonly repositoryName = "VenueBasedTicketBasketHoldsRepository";

  public readonly endpoint = venueBasedUrl("tickets/baskets/holds/");

  public get urlMap(): UrlRouteMap {
    return {
      ...this.defaultUrlMap,
      dynamic: `dynamic/{id}`,
      release: `{basketId}/release/`,
      split: `{basketId}/split/`,
      sendHoldLinkEmail: `{basketId}/send-hold-link-email/`,
      bulkSendHoldLinkEmail: "bulk-send-hold-link-email/",
    };
  }

  public async release(
    basketId: number,
    config: AxiosRequestConfig = {}
  ): Promise<GetTaskFromJobIdReturnType> {
    const { data } = await this.request<TaskedEndpointResponse>(
      this.buildUrl("release", { basketId }),
      {
        method: "post",
        ...config,
      }
    );

    return taskLoader(data.job_id);
  }

  public async split(
    basketId: number,
    splitData: VenueBasedTicketBasketHoldsSplitSerializer,
    config: AxiosRequestConfig = {}
  ) {
    const { data } = await this.request(this.buildUrl("split", { basketId }), {
      method: "post",
      data: splitData,
      ...config,
    });

    return data;
  }

  public async sendHoldLinkEmail(
    basketId: number,
    config: AxiosRequestConfig = {}
  ): Promise<void> {
    await this.request(this.buildUrl("sendHoldLinkEmail", { basketId }), {
      method: "post",
      ...config,
    });
  }

  public async bulkSendHoldLinkEmail(
    payload: BulkSendHoldLinkEmailPayload,
    config: AxiosRequestConfig = {}
  ): Promise<BulkSendHoldLinkEmailResponse> {
    const { data } = await this.request<BulkSendHoldLinkEmailResponse>(
      this.buildUrl("bulkSendHoldLinkEmail"),
      {
        method: "post",
        data: {
          basket_ids: payload.basketIds,
          message_to_customer: payload.messageToCustomer,
          ...(payload.language && { language: payload.language }),
        },
        ...config,
      }
    );
    return data;
  }
}
